import Fields from "./Fields"
import { useTranslation } from '../../StateManagment/TranslationContext';
import { useContext, useState } from 'react'
import { Responses } from "../../StateManagment/Responses";
import { timestamp, db } from '../../firebase/config';
import requiredFieldsBlock from '../../helpers/questionnaires/requiredFieldsBlock';
import requiredBlockMessage from './requiredBlockMessage';
import loadMatrixRows from './loadMatrixRows';
import PublicQuestionnaireContext from '../../StateManagment/PublicQuestionnaireContext';
import firebase from '../../firebase/compat';
import { client } from "../../hooks/Client";
import { QuestionnaireContext } from "../../StateManagment/Questionnaire";
import hiddenResponsesScope from './hiddenResponsesScope';
import withLinkedStep from '../../helpers/questionnaire/withLinkedStep';

/**
 * @param {object} props
 * @param {object} props.field - QuestionnaireFields doc for this question (ID, Question, Type, ...)
 * @param {number} props.index - Position of this field in the questionnaire
 * @param {string} props.compagnyId - Tenant CompagnyID of the questionnaire
 * @param {object} props.questionaire - Questionnaires doc being answered
 * @param {string} props.languageCode - Language code for translations
 * @param {string} props.moment - MomentID url param
 * @param {string} props.persona - PersonaID url param ('' when absent)
 * @param {string} props.questionnaireId - Questionnaires doc ID
 * @param {string} props.research - ResearchID url param
 * @param {string} props.sessionId - Respondent session uuid
 * @param {Array<string>} [props.hiddenFieldIDs] - Vragen die door een dichtgeklapte
 *   tak verborgen raakten; hun antwoorden worden in dezelfde aanroep opgeruimd
 */
const TopLevelFields = ({
  field,
  index,
  compagnyId, 
  questionaire, 
  languageCode, 
  moment,
  persona,
  questionnaireId,
  research,
  sessionId,
  hiddenFieldIDs
}) => {
  // Context
  const { isTransitioning, fieldIndex, setFieldIndex, settings } = useContext(QuestionnaireContext);
  const { translations } = useTranslation();
  const [responses ] = useContext(Responses);
  // Op de openbare pagina komen de matrixrijen uit getPublicQuestionnaire.
  const publicQuestionnaire = useContext(PublicQuestionnaireContext);
  const matrixRowLoader = publicQuestionnaire
      ? async (fieldID) => publicQuestionnaire.matrixRows[fieldID] || []
      : loadMatrixRows;

  // State
  const [saving, setSaving] = useState(false);
  const [blockMessage, setBlockMessage] = useState('');

  // Utils
  const isMatrixType = (t) => t === 'matrix-one' || t === 'matrix-multiple';

  // Server-side upsert. The public form no longer reads/writes QuestionnairesResponses
  // directly (that required world-open read/update rules); the Cloud Function does the
  // dedup + create-or-update as admin. See functions/questionnaires/saveQuestionnaireResponse.js
  const saveResponseFn = firebase.app().functions('europe-west1').httpsCallable('saveQuestionnaireResponse');

  // Save response(s) for the current fieldIndex. All answers of one field go
  // out in ONE call (responses: []) — a matrix question with N rows used to be
  // N sequential round-trips. The CF caps a batch at 50 entries, so chunk.
  const BATCH_LIMIT = 50;

  const saveResponse = async () => {
    // A save is already in flight — the buttons are disabled, but guard anyway
    if (saving) return;

    setBlockMessage('');

    // Collect responses for this field
    const fieldResponses = responses.filter(r => r.FieldID === field.ID);

    // Verplichte vragen: dezelfde poort als de sectieweergave gebruikt, zodat
    // de twee niet uit elkaar kunnen lopen. Een matrixvraag vraagt om álle
    // rijen — altijd bij een meetstandaard-vraag (MSIId), en bij elke
    // matrixvraag die de opsteller zelf verplicht stelde.
    // 'Bezig…' blijft staan van de controle tot en met de opslag. Hij ging
    // tussendoor even uit, waardoor de knop flikkerde.
    setSaving(true);
    let blokkade;
    try {
      blokkade = await requiredFieldsBlock([field], responses, matrixRowLoader);
    } catch (error) {
      setSaving(false);
      throw error;
    }
    if (blokkade) {
      setSaving(false);
      setBlockMessage(requiredBlockMessage(blokkade, translations));
      return;
    }

    // Antwoorden op vragen die door dít antwoord verdwenen zijn, gaan mee in
    // dezelfde batch: opslaan en opruimen kunnen zo niet uit elkaar lopen.
    // AI-CONTEXT: See docs/features/conditional-questions.md
    const hiddenScope = hiddenResponsesScope({
      hiddenFieldIDs,
      questionnaireId,
      moment,
      research,
      sessionId,
    });

    // Checkbox questions save one doc per selected option (Itemid identifies
    // the option) and need server-side cleanup of deselected options — so the
    // CF must be called even when nothing is checked. The `field` scope tells
    // it which same-session docs may be removed (Itemid not in KeepItemids).
    const isMultiMulti = field.Type === 'multiple-multiple';

    // If nothing to save, simply advance — tenzij er nog wezen op te ruimen zijn.
    if (fieldResponses.length === 0 && !isMultiMulti && !hiddenScope) {
      setSaving(false);
      setFieldIndex(fieldIndex + 1);
      return;
    }

    const items = fieldResponses.map((fieldResponse) => ({
      isMatrix: isMatrixType(fieldResponse.FieldType),
      response: {
        FieldID: field.ID,
        FieldTitle: field.Question,
        // ?? en niet ||: het antwoord 0 is een antwoord (#314).
        // AI-CONTEXT: See docs/pitfalls.md#state-or-fallback-erases-zero
        Input: fieldResponse?.Input ?? '',
        CompagnyID: client,
        QuestionannaireID: questionnaireId,
        MomentID: moment,
        ResearchID: research,
        FormID: sessionId,
        SessionID: sessionId,
        OptionType: fieldResponse?.OptionType || '',
        Persona: persona || '',
        RowTitle: fieldResponse?.RowTitle || '',
        RowTitleRight: fieldResponse?.RowTitleRight || '',
        RowID: fieldResponse?.RowID || '',
        FieldType: fieldResponse?.FieldType || '',
        ColumnID: fieldResponse?.ColumnID || '',
        Itemid: fieldResponse?.Itemid || '',
        Position: fieldIndex,
        // Plek op de schuifbalk; alleen als die er was. AI-CONTEXT: See docs/features/vas-slider.md
        ...(typeof fieldResponse?.VasRaw === 'number' ? { VasRaw: fieldResponse.VasRaw } : {}),
      }
    }));

    const cleanupScope = isMultiMulti
      ? {
          FieldID: field.ID,
          CompagnyID: client,
          QuestionannaireID: questionnaireId,
          MomentID: moment,
          ResearchID: research,
          SessionID: sessionId,
          Position: fieldIndex,
          FieldType: 'multiple-multiple',
          KeepItemids: fieldResponses.map(r => r?.Itemid).filter(Boolean)
        }
      : undefined;

    setSaving(true);

    try {
      if (items.length === 0) {
        // Everything deselected: no upserts, cleanup only
        await saveResponseFn(withLinkedStep({
          timestampMillis: timestamp.toMillis(),
          responses: [],
          field: cleanupScope,
          ...(hiddenScope ? { hidden: hiddenScope } : {})
        }, settings));
      }

      for (let start = 0; start < items.length; start += BATCH_LIMIT) {
        await saveResponseFn(withLinkedStep({
          timestampMillis: timestamp.toMillis(),
          responses: items.slice(start, start + BATCH_LIMIT),
          // Cleanup runs once, on the first chunk; KeepItemids covers the full
          // selection so later chunks' docs are never treated as deselected.
          ...(start === 0 && cleanupScope ? { field: cleanupScope } : {}),
          ...(start === 0 && hiddenScope ? { hidden: hiddenScope } : {})
        }, settings));
      }

      setFieldIndex(fieldIndex + 1);
    } finally {
      // Re-enable the buttons on failure too, so the respondent can retry
      setSaving(false);
    }
  };

  return (
    <div style={{ display: index !== fieldIndex ? 'none' : 'flex' }} className="top-level-field-container">
      <Fields 
        field={field} 
        compagnyId={compagnyId} 
        questionaire={questionaire}
        languageCode={languageCode}
        isVisible={index === fieldIndex}
      />

      {blockMessage && (
        <p className="questionnaire-rows-required" role="alert">
          {blockMessage}
        </p>
      )}

      <div className="questionnaire-button-container">
        <button
          style={{ display: fieldIndex === 0 ? 'none' : 'block' }}
          onClick={() => setFieldIndex(fieldIndex - 1)}
          disabled={isTransitioning || saving}
        >
          {translations?.["Terug"] || "Terug"}
        </button>

        <button
          onClick={saveResponse}
          disabled={isTransitioning || saving}
        >
          {/* Tekst, geen laad-icoon: de dunne grijze rimpel was op de grijze,
              uitgeschakelde knop onzichtbaar en de knop leek leeg (testronde
              2026-10-13, B14). Zelfde tekst als de naam- en adresstap. */}
          {saving ? (
            <span role="status">{translations?.["Bezig"] || "Bezig…"}</span>
          ) : (
            translations?.["Volgende"] || "Volgende"
          )}
        </button>
      </div>
    </div>
  );
};

export default TopLevelFields;
